<template>
    <div class="rent-view-container">
        <!-- Header Section -->
        <div class="header-section">
            <div class="header-content">
                <div class="title-section">
                    <div class="title-icon">
                        <img src="@/assets/rupeeSymbol.svg" alt="Rupee icon" class="logo-image" />
                    </div>
                    <div class="title-text">
                        <h1>Rent Payments</h1>
                        <p>Review rent dues and verify or reject guest-submitted payment proof</p>
                    </div>
                </div>
                <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Back
                </button>
            </div>

            <div class="rent-cron-bar">
                <div class="rent-cron-bar-text">
                    <span class="rent-cron-title">Automatic rent dues</span>
                    <span class="rent-cron-desc">
                        When on, rent payment due records are created for guests. When off, no new rent dues are generated.
                    </span>
                </div>
                <div class="rent-cron-controls">
                    <span class="rent-cron-label" :class="{ 'rent-cron-label--on': isRentCronYes && !isUpdatingRentCron, 'rent-cron-label--busy': isUpdatingRentCron }">
                        {{ isUpdatingRentCron ? 'Saving…' : (isRentCronYes ? 'On' : 'Off') }}
                    </span>
                    <div class="rent-cron-switch-wrap">
                        <button
                            type="button"
                            class="rent-cron-switch"
                            :class="{ 'rent-cron-switch--on': isRentCronYes, 'rent-cron-switch--busy': isUpdatingRentCron }"
                            :disabled="isUpdatingRentCron || !objectId"
                            role="switch"
                            :aria-busy="isUpdatingRentCron"
                            :aria-checked="isRentCronYes"
                            :aria-label="isRentCronYes ? 'Turn off automatic rent dues' : 'Turn on automatic rent dues'"
                            @click="openRentCronConfirm"
                        >
                            <span class="rent-cron-switch-knob" aria-hidden="true" />
                        </button>
                        <div v-if="isUpdatingRentCron" class="rent-cron-switch-loader" aria-hidden="true">
                            <span class="rent-cron-switch-spinner" />
                        </div>
                    </div>
                </div>
            </div>
            <p v-if="rentCronError" class="rent-cron-error">{{ rentCronError }}</p>
        </div>

        <div
            v-if="showRentCronConfirm"
            class="rent-cron-modal-overlay"
            @click.self="cancelRentCronConfirm"
        >
            <div class="rent-cron-modal" role="dialog" aria-modal="true" aria-labelledby="rent-cron-modal-title">
                <h3 id="rent-cron-modal-title" class="rent-cron-modal-title">{{ rentCronModalTitle }}</h3>
                <p class="rent-cron-modal-body">{{ rentCronModalBody }}</p>
                <div class="rent-cron-modal-actions">
                    <button type="button" class="rent-cron-btn-secondary" :disabled="isUpdatingRentCron" @click="cancelRentCronConfirm">
                        Cancel
                    </button>
                    <button type="button" class="rent-cron-btn-primary" :disabled="isUpdatingRentCron" @click="confirmRentCronChange">
                        {{ isUpdatingRentCron ? 'Saving…' : 'Confirm' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Rent payment proof verify modal -->
        <div
            v-if="verifyModalRent"
            class="rent-cron-modal-overlay rent-verify-overlay"
            @click.self="closeVerifyModal"
        >
            <div class="proof-modal" role="dialog" aria-modal="true" aria-labelledby="proof-modal-title" @click.stop>
                <header class="proof-modal__header">
                    <div class="proof-modal__header-text">
                        <h2 id="proof-modal-title" class="proof-modal__title">Payment proof</h2>
                        <p class="proof-modal__subtitle">
                            {{ verifyModalReadOnly ? 'View guest submission' : 'Verify or reject this submission' }}
                        </p>
                    </div>
                    <button
                        type="button"
                        class="proof-modal__close"
                        :disabled="verifyModalLoading"
                        aria-label="Close"
                        @click="closeVerifyModal"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/>
                        </svg>
                    </button>
                </header>

                <div v-if="verifyModalRent" class="proof-modal__body">
                    <div class="proof-modal__badges">
                        <span :class="['proof-modal__chip', 'proof-modal__chip--rent', getStatusClass(verifyModalRent.status)]">
                            <span class="status-dot" aria-hidden="true"></span>
                            {{ verifyModalRent.status || '—' }}
                        </span>
                        <span :class="['proof-modal__chip', 'proof-modal__chip--proof', getProofStatusClass(verifyModalRent.paymentproofstatus)]">
                            <span class="status-dot" aria-hidden="true"></span>
                            {{ formatProofStatus(verifyModalRent.paymentproofstatus) }}
                        </span>
                    </div>

                    <div class="proof-modal__guest">
                        <div class="proof-modal__avatar" aria-hidden="true">
                            {{ getInitials(verifyModalRent.firstName || verifyModalRent.objectName) }}
                        </div>
                        <div class="proof-modal__guest-info">
                            <span class="proof-modal__guest-name">{{ verifyModalRent.firstName || 'Guest' }}</span>
                            <span class="proof-modal__guest-line">
                                <span class="proof-modal__guest-phone">{{ verifyModalRent.mobileno || '—' }}</span>
                                <span class="proof-modal__guest-dot">·</span>
                                <span>{{ formatPeriod(verifyModalRent.month, verifyModalRent.year) }}</span>
                                <span class="proof-modal__guest-dot">·</span>
                                <strong class="proof-modal__guest-amount">{{ formatCurrency(verifyModalRent.amount, verifyModalRent.currency) }}</strong>
                            </span>
                        </div>
                    </div>

                    <div class="proof-modal__grid">
                        <section class="proof-modal__viewer" aria-label="Proof attachment">
                            <div v-if="verifyModalRent.paymentproof" class="proof-modal__viewer-block">
                                <div class="proof-modal__section-head">
                                    <span class="proof-modal__section-title">Proof file</span>
                                    <span
                                        v-if="isImageUrl(verifyModalRent.paymentproof)"
                                        class="proof-modal__section-hint"
                                    >Click image to view full size</span>
                                </div>
                                <div class="proof-modal__viewer-shell">
                                    <div class="proof-modal__viewer-canvas">
                                        <button
                                            v-if="isImageUrl(verifyModalRent.paymentproof)"
                                            type="button"
                                            class="proof-modal__img-trigger"
                                            aria-label="View payment proof full size"
                                            @click="openProofLightbox"
                                        >
                                            <img
                                                :src="verifyModalRent.paymentproof"
                                                alt="Payment proof — click to enlarge"
                                                class="proof-modal__img"
                                            />
                                        </button>
                                        <div v-else class="proof-modal__file-block">
                                            <svg class="proof-modal__file-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linejoin="round"/>
                                                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke-linecap="round"/>
                                            </svg>
                                            <p class="proof-modal__file-text">This file type cannot be previewed here.</p>
                                            <a
                                                :href="verifyModalRent.paymentproof"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="proof-modal__file-cta"
                                            >Open file</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="proof-modal__viewer-empty">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="1.5">
                                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                </svg>
                                <p>No file uploaded yet.</p>
                            </div>
                        </section>

                        <aside class="proof-modal__aside" aria-label="Payment details">
                            <div class="proof-modal__section-head proof-modal__section-head--aside">
                                <span class="proof-modal__section-title">Payment details</span>
                            </div>
                            <ul class="proof-modal__kv">
                                <li v-if="verifyModalRent.bill_date"><span>Bill date</span><span>{{ formatDate(verifyModalRent.bill_date) }}</span></li>
                                <li v-if="verifyModalRent.manualpaymenttype"><span>Payment type</span><span>{{ formatEnumLabel(verifyModalRent.manualpaymenttype) }}</span></li>
                                <li v-if="verifyModalRent.prooftype"><span>Proof type</span><span>{{ formatEnumLabel(verifyModalRent.prooftype) }}</span></li>
                                <li v-if="verifyModalRent.paymentproofuploadedon"><span>Uploaded</span><span>{{ formatDate(verifyModalRent.paymentproofuploadedon) }}</span></li>
                                <li v-if="verifyModalRent.verifiedon"><span>Verified</span><span>{{ formatDate(verifyModalRent.verifiedon) }}</span></li>
                            </ul>
                            <div v-if="verifyModalRent.proofnotes" class="proof-modal__note">
                                <span class="proof-modal__note-label">Guest note</span>
                                <p class="proof-modal__note-body">{{ verifyModalRent.proofnotes }}</p>
                            </div>
                            <div v-if="verifyModalRent.rejectionreason" class="proof-modal__note proof-modal__note--warn">
                                <span class="proof-modal__note-label">Rejection reason</span>
                                <p class="proof-modal__note-body">{{ verifyModalRent.rejectionreason }}</p>
                            </div>
                        </aside>
                    </div>

                    <p v-if="verifyModalError" class="proof-modal__error">{{ verifyModalError }}</p>

                    <div v-if="verifyModalRejectStep" class="proof-modal__reject">
                        <label class="proof-modal__reject-label" for="reject-reason-input">Reason for rejection</label>
                        <textarea
                            id="reject-reason-input"
                            v-model="verifyRejectReason"
                            class="proof-modal__textarea"
                            rows="2"
                            placeholder="Brief reason shown to operations / guest flow"
                        />
                    </div>

                    <footer class="proof-modal__footer">
                        <button type="button" class="proof-modal__btn proof-modal__btn--ghost" :disabled="verifyModalLoading" @click="closeVerifyModal">
                            Close
                        </button>
                        <div class="proof-modal__footer-actions">
                            <template v-if="verifyModalActionsVisible">
                                <template v-if="!verifyModalRejectStep">
                                    <button type="button" class="proof-modal__btn proof-modal__btn--danger" :disabled="verifyModalLoading" @click="startRejectStep">
                                        Reject
                                    </button>
                                    <button
                                        type="button"
                                        class="proof-modal__btn proof-modal__btn--primary"
                                        :disabled="verifyModalLoading || !verifyModalRent.paymentproof"
                                        @click="submitVerifyVerified"
                                    >
                                        {{ verifyModalLoading ? 'Saving…' : 'Verify payment' }}
                                    </button>
                                </template>
                                <template v-else>
                                    <button type="button" class="proof-modal__btn proof-modal__btn--ghost" :disabled="verifyModalLoading" @click="cancelRejectStep">
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        class="proof-modal__btn proof-modal__btn--danger"
                                        :disabled="verifyModalLoading || !verifyRejectReason.trim()"
                                        @click="submitVerifyRejected"
                                    >
                                        {{ verifyModalLoading ? 'Saving…' : 'Confirm reject' }}
                                    </button>
                                </template>
                            </template>
                        </div>
                    </footer>
                </div>
            </div>

            <!-- Full-size proof image (click thumbnail in modal) -->
            <div
                v-if="proofLightboxOpen && verifyModalRent && isImageUrl(verifyModalRent.paymentproof)"
                class="proof-lightbox"
                role="dialog"
                aria-modal="true"
                aria-label="Full size payment proof"
                @click.self="closeProofLightbox"
            >
                <button type="button" class="proof-lightbox__close" aria-label="Close full size view" @click="closeProofLightbox">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/>
                    </svg>
                </button>
                <div class="proof-lightbox__frame" @click.stop>
                    <img
                        :src="verifyModalRent.paymentproof"
                        alt="Payment proof full size"
                        class="proof-lightbox__img"
                    />
                </div>
                <p class="proof-lightbox__hint">Click outside image to close</p>
            </div>
        </div>

        <!-- Filters and Search Section -->
        <div class="filters-section">
            <div class="search-container">
                <div class="search-input-wrapper">
                    <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                        <path d="m21 21-4.35-4.35" stroke="currentColor" stroke-width="2"/>
                    </svg>
                    <input 
                        type="text" 
                        class="search-input" 
                        placeholder="Search by mobile number"
                        v-model="searchQuery"
                        @input="handleSearch"
                    />
                </div>
            </div>
            <div class="filter-controls">
                <select class="filter-select" v-model="statusFilter" @change="handleFilterChange">
                    <option value="">All rent status</option>
                    <option value="PAID">Paid</option>
                    <option value="DUE">Due</option>
                    <option value="FAILED">Failed</option>
                </select>
                <select class="filter-select" v-model="proofStatusFilter" @change="handleFilterChange">
                    <option value="">All proof status</option>
                    <option value="PENDING">Pending</option>
                    <option value="REVIEW">Review</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="REJECTED">Rejected</option>
                </select>
                <select class="filter-select" v-model="monthFilter" @change="handleFilterChange">
                    <option value="">All months (BS)</option>
                    <option v-for="m in nepaliMonthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                </select>
                <select class="filter-select" v-model="yearFilter" @change="handleFilterChange">
                    <option value="">All years (BS)</option>
                    <option v-for="year in nepaliYearFilterOptions" :key="year" :value="String(year)">{{ year }}</option>
                </select>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-section">
            <div class="stat-card">
                <div class="stat-icon paid">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ paidCount }}</h3>
                    <p>Paid</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon due">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ dueCount }}</h3>
                    <p>Due</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon pending-proof">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ pendingProofReviewCount }}</h3>
                    <p>Awaiting proof review</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon total">
                    <img src="@/assets/rupeeSymbol.svg" alt="Rupee icon" class="logo-image" />
                </div>
                <div class="stat-content">
                    <h3>{{ totalAmount }}</h3>
                    <p>Total paid (current month/year)</p>
                </div>
            </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
            <div class="table-header">
                <h2>Payment Records</h2>
                <div class="table-actions">
                    <button class="export-btn" @click="exportData">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" stroke-width="2"/>
                        </svg>
                        Export
                    </button>
                </div>
            </div>
            
            <div class="modern-table-container" :class="{ 'table-loading': isLoadingTableData }">
                <!-- Table Loading Overlay -->
                <div class="table-loading-overlay" v-if="isFetching">
                    <div class="table-spinner">
                        <div class="spinner-circle-small"></div>
                        <span class="loading-text-small">Loading payments...</span>
                    </div>
                </div>
                <div class="table-wrapper">
                    <table class="modern-table">
                        <thead v-if="filteredRents.length > 0 && !isFetching">
                            <tr>
                                <th>Guest</th>
                                <th class="sortable" @click="sortBy('amount')">
                                    Amount
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Period</th>
                                <th>Rent</th>
                                <th>Payment Proof Status</th>
                                <th class="sortable" @click="sortBy('paymentproofuploadedon')">
                                    Proof uploaded
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th class="sortable" @click="sortBy('paidon')">
                                    Paid on
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="rent in filteredRents" :key="rent.rentpaymentid" class="table-row">
                                <td class="guest-info">
                                    <div class="guest-avatar">
                                        {{ getInitials(rent.firstName || rent.objectName) }}
                                    </div>
                                    <div class="guest-details">
                                        <span class="guest-name">{{ rent.firstName || 'Unknown' }}</span>
                                        <span class="guest-mobile">{{ rent.mobileno || '—' }}</span>
                                    </div>
                                </td>
                                <td class="amount-cell">
                                    <span class="amount">{{ formatCurrency(rent.amount, rent.currency) }}</span>
                                </td>
                                <td class="period-cell">
                                    <span class="period">{{ formatPeriod(rent.month, rent.year) }}</span>
                                </td>
                                <td>
                                    <span :class="['modern-status-badge', getStatusClass(rent.status)]">
                                        <span class="status-dot"></span>
                                        {{ rent.status || '—' }}
                                    </span>
                                </td>
                                <td>
                                    <span :class="['modern-status-badge', 'proof-badge', getProofStatusClass(rent.paymentproofstatus)]">
                                        <span class="status-dot"></span>
                                        {{ formatProofStatus(rent.paymentproofstatus) }}
                                    </span>
                                </td>
                                <td class="date-cell">
                                    {{ formatDate(rent.paymentproofuploadedon) }}
                                </td>
                                <td class="date-cell">
                                    {{ formatDate(rent.paidon) }}
                                </td>
                                <td class="actions-cell">
                                    <button
                                        v-if="canReviewProof(rent)"
                                        type="button"
                                        class="review-proof-btn"
                                        @click="openVerifyModal(rent)"
                                    >
                                        Review proof
                                    </button>
                                    <button
                                        v-else-if="rent.paymentproof"
                                        type="button"
                                        class="view-proof-btn"
                                        @click="openVerifyModal(rent, true)"
                                    >
                                        View proof
                                    </button>
                                    <span v-else class="actions-placeholder">—</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <!-- Empty State -->
                    <div v-if="filteredRents.length === 0 && !isFetching" class="empty-state">
                        <div class="empty-icon">
                            <img src="@/assets/rent.svg" alt="No payments illustration" class="empty-illustration-img" />
                        </div>
                        <h3>No payments found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modern Pagination -->
        <div class="modern-pagination" v-if="totalCount > pageSize">
            <div class="pagination-info">
                <span>Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} results</span>
            </div>
            <div class="pagination-controls">
                <button 
                    :disabled="currentPage === 1 || isFetching || isLoadingTableData" 
                    @click="changePage(currentPage - 1)"
                    class="pagination-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
                
                <div class="page-numbers">
                    <button 
                        v-for="page in visiblePages" 
                        :key="page"
                        :class="['page-btn', { 'active': page === currentPage, 'ellipsis': page === '...' }]" 
                        :disabled="isFetching || isLoadingTableData || page === '...'"
                        @click="changePage(page)">
                        {{ page }}
                    </button>
                </div>
                
                <button 
                    :disabled="currentPage === totalPages || isFetching || isLoadingTableData" 
                    @click="changePage(currentPage + 1)"
                    class="pagination-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import NepaliDate from 'nepali-date-converter';
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';

/** BS month values as returned by the rent listing API (EQUAL filter). */
const NEPALI_BS_MONTH_OPTIONS = [
    { value: 'BAISAKH', label: 'Baisakh' },
    { value: 'JESTHA', label: 'Jestha' },
    { value: 'ASHADH', label: 'Ashadh' },
    { value: 'SHRAWAN', label: 'Shrawan' },
    { value: 'BHADRA', label: 'Bhadra' },
    { value: 'ASHWIN', label: 'Ashwin' },
    { value: 'KARTIK', label: 'Kartik' },
    { value: 'MANGSIR', label: 'Mangsir' },
    { value: 'PAUSH', label: 'Paush' },
    { value: 'MAGH', label: 'Magh' },
    { value: 'FALGUN', label: 'Falgun' },
    { value: 'CHAITRA', label: 'Chaitra' },
];

/** Default rent-payment filters: today's BS month/year (month index matches `NEPALI_BS_MONTH_OPTIONS`). */
function defaultBsMonthYearFilters() {
    const nd = new NepaliDate();
    const monthOpt = NEPALI_BS_MONTH_OPTIONS[nd.getMonth()];
    return {
        monthFilter: monthOpt ? monthOpt.value : '',
        yearFilter: String(nd.getYear()),
    };
}

export default {
    name: 'RentView',
    emits: ['addRent', 'showOnboardAddForm', 'rentcron-updated'],
    components: {
    },
    props: {
        objectSpaces: {
            type: Array,
            required: true,
        },
        objectData: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            objectName: '',
            adminProfileData: {},
            rents: [],
            currentPage: 1,
            pageSize: this.getInitialPageSize(),
            totalCount: 0,
            totalPages: 0,
            isFetching: false,
            isLoadingTableData: false,
            objectId: '',
            cacheDuration: 5 * 60 * 1000, // Cache duration: 5 minutes in milliseconds
            searchQuery: '',
            statusFilter: '',
            proofStatusFilter: '',
            ...defaultBsMonthYearFilters(),
            nepaliMonthOptions: NEPALI_BS_MONTH_OPTIONS,
            sortField: '',
            sortDirection: 'asc',
            searchDebounceTimer: null,
            resizeTimer: null,
            showRentCronConfirm: false,
            rentCronPendingValue: 'NO',
            isUpdatingRentCron: false,
            rentCronError: '',
            verifyModalRent: null,
            verifyModalReadOnly: false,
            verifyModalRejectStep: false,
            verifyRejectReason: '',
            verifyModalLoading: false,
            verifyModalError: '',
            proofLightboxOpen: false,
            /** Populated from list API `stats`; when null, card counts fall back to the current page rows. */
            rentListStats: null,
        };
    },
    computed: {
        isRentCronYes() {
            const raw = this.objectData?.rentcron ?? this.objectData?.rentCron;
            return String(raw ?? '').trim().toUpperCase() === 'YES';
        },
        rentCronModalTitle() {
            return this.rentCronPendingValue === 'YES'
                ? 'Turn on automatic rent dues?'
                : 'Turn off automatic rent dues?';
        },
        rentCronModalBody() {
            return this.rentCronPendingValue === 'YES'
                ? 'The system will create rent payment due records for guests according to your property rules. You can turn this off at any time.'
                : 'The system will stop creating new rent due records for guests. Existing rent payment rows in this list are not removed.';
        },
        visiblePages() {
            const range = 2;
            let pages = [];
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            if (end < this.totalPages - 1) pages.push('...');

            if (!pages.includes(1) && start > 1) pages.unshift(1);
            if (!pages.includes(this.totalPages) && end < this.totalPages) pages.push(this.totalPages);

            return pages;
        },
        filteredRents() {
            let filtered = [...this.rents];

            // Apply search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(rent => 
                    (rent.rentpaymentid && rent.rentpaymentid.toString().toLowerCase().includes(query)) ||
                    (rent.firstName && rent.firstName.toLowerCase().includes(query)) ||
                    (rent.objectName && rent.objectName.toLowerCase().includes(query)) ||
                    (rent.amount && rent.amount.toString().includes(query)) ||
                    (rent.mobileno && rent.mobileno.toString().includes(query)) ||
                    (rent.guestId && rent.guestId.toString().toLowerCase().includes(query))
                );
            }

            // Rent status, proof status, BS month/year are applied server-side via columnFilters.

            // Apply sorting
            if (this.sortField) {
                filtered.sort((a, b) => {
                    let aVal = a[this.sortField];
                    let bVal = b[this.sortField];

                    if (this.sortField === 'amount') {
                        aVal = parseFloat(aVal) || 0;
                        bVal = parseFloat(bVal) || 0;
                    } else if (this.sortField === 'paidon' || this.sortField === 'createdat' || this.sortField === 'paymentproofuploadedon' || this.sortField === 'bill_date') {
                        aVal = aVal ? new Date(aVal) : new Date(0);
                        bVal = bVal ? new Date(bVal) : new Date(0);
                    } else {
                        aVal = aVal?.toString().toLowerCase() || '';
                        bVal = bVal?.toString().toLowerCase() || '';
                    }

                    if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
                    if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
                    return 0;
                });
            }

            return filtered;
        },
        /** BS years for filter dropdown; merges API data with a fixed range. */
        nepaliYearFilterOptions() {
            const fromApi = [...new Set(this.rents.map((r) => r.year).filter((y) => y !== undefined && y !== null && y !== ''))].map((y) => String(y));
            const staticRange = [];
            for (let y = 2099; y >= 2075; y -= 1) {
                staticRange.push(String(y));
            }
            return [...new Set([...fromApi, ...staticRange])].sort((a, b) => Number(b) - Number(a));
        },
        paidCount() {
            if (this.rentListStats) return this.rentListStats.paidCount;
            return this.rents.filter((rent) => rent.status === 'PAID').length;
        },
        dueCount() {
            if (this.rentListStats) return this.rentListStats.dueCount;
            return this.rents.filter((rent) => rent.status === 'DUE').length;
        },
        pendingProofReviewCount() {
            if (this.rentListStats) return this.rentListStats.reviewCount;
            return this.rents.filter((rent) => this.canReviewProof(rent)).length;
        },
        totalAmount() {
            if (this.rentListStats) {
                return this.formatCurrency(
                    this.rentListStats.totalPaidAmount,
                    this.rents[0]?.currency || 'NPR',
                );
            }
            const total = this.rents.reduce((sum, rent) => sum + (parseFloat(rent.amount) || 0), 0);
            return this.formatCurrency(total, this.rents[0]?.currency || 'NPR');
        },
        verifyModalActionsVisible() {
            return !!(this.verifyModalRent && !this.verifyModalReadOnly && this.canReviewProof(this.verifyModalRent));
        },
    },
    mounted() {
        this.objectName = this.$route.params.objectName;
        this.objectId = this.$route.params.id;
        let adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfileData);
        // Force fresh API call on mount (page refresh)
        window.addEventListener('beforeunload', this.clearCache);
        // Update page size based on screen size
        this.updatePageSize();
        window.addEventListener('resize', this.handleResize);
        this.fetchRents();
    },
    beforeUnmount() {
        // Clean up the event listener
        window.removeEventListener('beforeunload', this.clearCache);
        window.removeEventListener('resize', this.handleResize);
        this.closeProofLightbox();
    },
    methods: {
        openRentCronConfirm() {
            this.rentCronError = '';
            this.rentCronPendingValue = this.isRentCronYes ? 'NO' : 'YES';
            this.showRentCronConfirm = true;
        },
        cancelRentCronConfirm() {
            if (this.isUpdatingRentCron) return;
            this.showRentCronConfirm = false;
        },
        async confirmRentCronChange() {
            if (!this.objectId) return;
            this.rentCronError = '';
            try {
                this.isUpdatingRentCron = true;
                await apiService.updateObject(this.objectId, { rentcron: this.rentCronPendingValue });
                this.showRentCronConfirm = false;
                this.$emit('rentcron-updated');
            } catch (error) {
                let message = 'Could not update rent due settings. Please try again.';
                if (error?.response?.data?.reason) {
                    message = error.response.data.reason;
                } else if (error?.response?.data?.message) {
                    message = error.response.data.message;
                }
                this.rentCronError = message;
            } finally {
                this.isUpdatingRentCron = false;
            }
        },
        goBack() {
            this.$router.go(-1);
        },
        handleAddRent(formData) {
            // Emit the form data to the parent component
            this.$emit('addRent', formData);
        },
        handleRentOnboarding(formData) {
            // Emit the form data to the parent component
            this.$emit('showOnboardAddForm', formData);
        },
        clearCache() {
            if (!this.objectId) return;
            const prefix = `RENT-${this.objectId}-`;
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith(prefix)) {
                    localStorage.removeItem(key);
                }
            });
        },
        /** Apply GET /rent/payment (or cached) body: `data`, `totalCount`, optional `stats`. */
        applyRentPaymentListResponse(payload) {
            const list = Array.isArray(payload?.data) ? payload.data : [];
            const total = Number(payload?.totalCount);
            this.rents = list;
            this.totalCount = Number.isFinite(total) ? total : list.length;
            this.totalPages = this.totalCount ? Math.ceil(this.totalCount / this.pageSize) : 0;
            const s = payload?.stats;
            if (s && typeof s === 'object') {
                this.rentListStats = {
                    paidCount: Number(s.paidCount) || 0,
                    dueCount: Number(s.dueCount) || 0,
                    reviewCount: Number(s.reviewCount) || 0,
                    totalPaidAmount: Number(s.totalPaidAmount) || 0,
                };
            } else {
                this.rentListStats = null;
            }
        },
        /** Rent listing query params (flat filters, e.g. paymentproofstatus=VERIFIED). */
        buildRentPaymentParams() {
            const params = {
                objectId: this.objectId,
                page: this.currentPage,
                pagesize: this.pageSize,
            };
            const q = (this.searchQuery || '').trim();
            if (q && (q.match(/^\d+$/) || q.length >= 10)) {
                params.guestMobileno = q;
            }
            if (this.statusFilter) {
                params.status = String(this.statusFilter).toUpperCase();
            }
            if (this.proofStatusFilter) {
                params.paymentproofstatus = String(this.proofStatusFilter).toUpperCase();
            }
            if (this.monthFilter) {
                params.month = String(this.monthFilter).toUpperCase();
            }
            if (this.yearFilter) {
                params.year = String(this.yearFilter);
            }
            return params;
        },
        getRentListCacheKey() {
            const q = (this.searchQuery || '').trim();
            const mobileToken = q && (q.match(/^\d+$/) || q.length >= 10) ? q : '-';
            const sig = [
                this.pageSize,
                this.statusFilter || '-',
                this.proofStatusFilter || '-',
                (this.monthFilter || '-').toUpperCase(),
                this.yearFilter || '-',
                mobileToken,
            ].join('|');
            return `RENT-${this.objectId}-${sig}-${this.currentPage}`;
        },
        async fetchRentsData(forceFresh = false) {
            const cacheKey = this.getRentListCacheKey();
            const cachedData = localStorage.getItem(cacheKey);

            // Skip cache if forceFresh is true (e.g., on page refresh)
            if (!forceFresh && cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    // Use cached data
                    this.applyRentPaymentListResponse(data);
                    return;
                }
            }

            // Fetch from API
            try {
                this.isLoadingTableData = true;
                const params = this.buildRentPaymentParams();

                const response = await apiService.getRentPayments(params);

                // Store response in cache with timestamp
                const cacheData = {
                    data: response.data,
                    timestamp: new Date().getTime(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

                this.applyRentPaymentListResponse(response.data);
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.rents = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                    this.rentListStats = null;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Error fetching rent payment:', error);
                    // Handle error (e.g., show error message)
                }
            } finally {
                this.isLoadingTableData = false;
            }
        },
        async fetchRents(forceFresh = false) {
            const cacheKey = this.getRentListCacheKey();
            const cachedData = localStorage.getItem(cacheKey);

            // Skip cache if forceFresh is true (e.g., on page refresh)
            if (!forceFresh && cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    // Use cached data
                    this.applyRentPaymentListResponse(data);
                    this.isFetching = false;
                    return;
                }
            }

            // Fetch from API
            try {
                this.isFetching = true;
                const params = this.buildRentPaymentParams();

                const response = await apiService.getRentPayments(params);

                // Store response in cache with timestamp
                const cacheData = {
                    data: response.data,
                    timestamp: new Date().getTime(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

                this.applyRentPaymentListResponse(response.data);
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.rents = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                    this.rentListStats = null;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Error fetching rent payment:', error);
                    // Handle error (e.g., show error message)
                }
            } finally {
                this.isFetching = false;
            }
        },
        getInitialPageSize() {
            // Set initial page size based on screen width
            if (typeof window !== 'undefined') {
                return window.innerWidth >= 768 ? 50 : 10;
            }
            return 50; // Default to desktop size
        },
        updatePageSize() {
            const newPageSize = window.innerWidth >= 768 ? 50 : 10;
            if (this.pageSize !== newPageSize) {
                this.pageSize = newPageSize;
                this.currentPage = 1; // Reset to first page when page size changes
                // Clear all cache entries when page size changes
                if (this.objectId) {
                    const prefix = `RENT-${this.objectId}-`;
                    Object.keys(localStorage).forEach((key) => {
                        if (key.startsWith(prefix)) {
                            localStorage.removeItem(key);
                        }
                    });
                }
            }
        },
        handleResize() {
            // Debounce resize handler
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                const oldPageSize = this.pageSize;
                this.updatePageSize();
                // Only refetch if page size actually changed
                if (oldPageSize !== this.pageSize) {
                    this.fetchRents(true); // Force fresh fetch
                }
            }, 300);
        },
        changePage(page) {
            if (
                (typeof page === 'number' && page >= 1 && page <= this.totalPages && !this.isFetching && !this.isLoadingTableData) ||
                (page === '...' && !this.isFetching && !this.isLoadingTableData)
            ) {
                this.currentPage = page;
                this.fetchRents(true); // Force fresh fetch when changing pages
            }
        },
        formatDate(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        canReviewProof(rent) {
            if (!rent) return false;
            const ps = String(rent.paymentproofstatus || '').toUpperCase();
            if (ps === 'VERIFIED' || ps === 'REJECTED') return false;
            if (!rent.paymentproof) return false;
            return ps === 'PENDING' || ps === 'REVIEW';
        },
        openVerifyModal(rent, readOnly = false) {
            this.closeProofLightbox();
            this.verifyModalError = '';
            this.verifyModalRejectStep = false;
            this.verifyRejectReason = '';
            this.verifyModalRent = rent;
            this.verifyModalReadOnly = !!readOnly;
        },
        closeVerifyModal(force = false) {
            if (!force && this.verifyModalLoading) return;
            this.closeProofLightbox();
            this.verifyModalRent = null;
            this.verifyModalReadOnly = false;
            this.verifyModalRejectStep = false;
            this.verifyRejectReason = '';
            this.verifyModalError = '';
            this.verifyModalLoading = false;
        },
        openProofLightbox() {
            if (!this.verifyModalRent?.paymentproof || !this.isImageUrl(this.verifyModalRent.paymentproof)) return;
            this.proofLightboxOpen = true;
            this._onProofLightboxKeydown = (e) => {
                if (e.key === 'Escape') {
                    this.closeProofLightbox();
                }
            };
            document.addEventListener('keydown', this._onProofLightboxKeydown);
        },
        closeProofLightbox() {
            if (this._onProofLightboxKeydown) {
                document.removeEventListener('keydown', this._onProofLightboxKeydown);
                this._onProofLightboxKeydown = null;
            }
            this.proofLightboxOpen = false;
        },
        cancelRejectStep() {
            this.verifyModalRejectStep = false;
            this.verifyRejectReason = '';
            this.verifyModalError = '';
        },
        startRejectStep() {
            this.verifyModalRejectStep = true;
            this.verifyModalError = '';
        },
        rentVerifyApiError(error) {
            const d = error?.response?.data;
            return d?.reason || d?.message || 'Something went wrong. Please try again.';
        },
        async submitVerifyVerified() {
            if (!this.verifyModalRent?.rentpaymentid || !this.objectId) return;
            this.verifyModalError = '';
            try {
                this.verifyModalLoading = true;
                await apiService.verifyRentPayment(this.verifyModalRent.rentpaymentid, this.objectId, {
                    paymentproofstatus: 'VERIFIED',
                });
                this.clearCache();
                await this.fetchRents(true);
                this.closeVerifyModal(true);
            } catch (error) {
                this.verifyModalError = this.rentVerifyApiError(error);
            } finally {
                this.verifyModalLoading = false;
            }
        },
        async submitVerifyRejected() {
            if (!this.verifyModalRent?.rentpaymentid || !this.objectId) return;
            const reason = this.verifyRejectReason.trim();
            if (!reason) return;
            this.verifyModalError = '';
            try {
                this.verifyModalLoading = true;
                await apiService.verifyRentPayment(this.verifyModalRent.rentpaymentid, this.objectId, {
                    paymentproofstatus: 'REJECTED',
                    rejectionreason: reason,
                });
                this.clearCache();
                await this.fetchRents(true);
                this.closeVerifyModal(true);
            } catch (error) {
                this.verifyModalError = this.rentVerifyApiError(error);
            } finally {
                this.verifyModalLoading = false;
            }
        },
        formatProofStatus(status) {
            const s = String(status || '').toUpperCase();
            if (!s) return '—';
            const labels = {
                PENDING: 'Pending',
                REVIEW: 'Review',
                VERIFIED: 'Verified',
                REJECTED: 'Rejected',
            };
            return labels[s] || s;
        },
        getProofStatusClass(status) {
            const s = String(status || '').toUpperCase();
            if (s === 'VERIFIED') return 'status-proof-verified';
            if (s === 'REJECTED') return 'status-proof-rejected';
            if (s === 'REVIEW') return 'status-proof-review';
            if (s === 'PENDING') return 'status-proof-pending';
            return 'status-proof-none';
        },
        isImageUrl(url) {
            if (!url || typeof url !== 'string') return false;
            const u = url.split('?')[0].toLowerCase();
            return /\.(jpe?g|png|gif|webp|bmp)$/i.test(u);
        },
        formatEnumLabel(raw) {
            if (!raw) return '';
            return String(raw)
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase());
        },
        handleSearch() {
            // Debounce search to avoid too many API calls
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.currentPage = 1; // Reset to first page when searching
                
                // If search looks like a mobile number (digits only and reasonable length), 
                // trigger API call to search by mobile number
                if (this.searchQuery && (this.searchQuery.match(/^\d+$/) || this.searchQuery.length >= 10)) {
                    this.clearCache(); // Clear cache to force fresh API call
                    this.fetchRentsData(); // Only fetch table data, don't refresh entire component
                } else if (!this.searchQuery) {
                    // If search is cleared, fetch fresh data
                    this.clearCache();
                    this.fetchRentsData();
                }
                // For other searches, rely on client-side filtering (filteredRents computed property)
            }, 300);
        },
        handleFilterChange() {
            this.currentPage = 1;
            this.clearCache();
            this.fetchRents(true);
        },
        sortBy(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDirection = 'asc';
            }
        },
        getStatusClass(status) {
            switch (status?.toUpperCase()) {
                case 'PAID':
                    return 'status-paid';
                case 'DUE':
                    return 'status-due';
                case 'FAILED':
                    return 'status-failed';
                case 'REVIEW':
                    return 'status-rent-review';
                default:
                    return 'status-unknown';
            }
        },
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        formatCurrency(amount, currency = 'NPR') {
            if (!amount && amount !== 0) return '--';

            const numAmount = parseFloat(amount);
            const code = (currency && String(currency).trim()) || 'NPR';
            try {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: code,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                }).format(numAmount);
            } catch (e) {
                return `${code} ${numAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
            }
        },
        formatPeriod(month, year) {
            if (!month && !year) return '--';
            if (!year) return String(month);
            if (!month) return String(year);
            const m = String(month).trim();
            if (/^\d+$/.test(m)) {
                const monthNames = [
                    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
                ];
                const monthIndex = parseInt(m, 10) - 1;
                if (monthIndex >= 0 && monthIndex < 12) {
                    return `${monthNames[monthIndex]} ${year}`;
                }
            }
            return `${m} ${year}`;
        },
        exportData() {
            const headers = [
                'Guest',
                'Mobile',
                'Period',
                'Amount',
                'Currency',
                'Rent status',
                'Proof status',
                'Proof URL',
                'Proof uploaded',
                'Paid on',
                'Bill date',
                'Guest note',
                'Rejection reason',
            ];

            const csvContent = [
                headers.join(','),
                ...this.filteredRents.map((rent) =>
                    [
                        `"${(rent.firstName || '').replace(/"/g, '""')}"`,
                        rent.mobileno || '',
                        `"${this.formatPeriod(rent.month, rent.year).replace(/"/g, '""')}"`,
                        rent.amount ?? '',
                        rent.currency || '',
                        rent.status || '',
                        rent.paymentproofstatus || '',
                        rent.paymentproof ? `"${String(rent.paymentproof).replace(/"/g, '""')}"` : '',
                        rent.paymentproofuploadedon || '',
                        rent.paidon || '',
                        rent.bill_date || '',
                        rent.proofnotes ? `"${String(rent.proofnotes).replace(/"/g, '""')}"` : '',
                        rent.rejectionreason ? `"${String(rent.rejectionreason).replace(/"/g, '""')}"` : '',
                    ].join(','),
                ),
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `rent-payments-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
/* Main Container */
.rent-view-container {
    padding: 0;
    background: #f8fafc;
    height: calc(100vh - 60px); /* Account for DashboardHeader */
    position: relative;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

@media (max-width: 768px) {
    .rent-view-container {
        height: calc(100vh - 60px);
        overflow: hidden;
    }
}

@media (max-width: 480px) {
    .rent-view-container {
        height: auto;
        min-height: calc(100vh - 60px);
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
    }
}

/* Loading Overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    border-radius: 16px;
}

.modern-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.spinner-circle {
    width: 48px;
    height: 48px;
    border: 3px solid #e2e8f0;
    border-top: 3px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    color: #6b7280;
    font-size: 16px;
    font-weight: 500;
    margin: 0;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Header Section */
.header-section {
    background: white;
    border-radius: 0;
    padding: 16px 24px;
    margin-bottom: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: none;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    margin-top: 20px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.title-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.logo-image {
    width: 20px;
    height: 20px;
    filter: brightness(0) saturate(100%) invert(100%);
    object-fit: contain;
}

.title-text {
    text-align: left;
}

.title-text h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.025em;
    text-align: left;
}

.title-text p {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    font-weight: 400;
    text-align: left;
}

.back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.back-btn:hover {
    border-color: #5b2e8e;
    color: #5b2e8e;
    background: #f8fafc;
}

.rent-cron-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    flex-wrap: wrap;
}

.rent-cron-bar-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    text-align: left;
}

.rent-cron-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.rent-cron-desc {
    font-size: 13px;
    color: #64748b;
    line-height: 1.45;
    max-width: 520px;
}

.rent-cron-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.rent-cron-label--busy {
    color: #5b2e8e !important;
    font-weight: 700;
    min-width: 52px;
    text-align: right;
    animation: rent-cron-label-pulse 1.2s ease-in-out infinite;
}

@keyframes rent-cron-label-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.65;
    }
}

.rent-cron-switch-wrap {
    position: relative;
    flex-shrink: 0;
    width: 48px;
    height: 28px;
}

.rent-cron-switch-loader {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 2;
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.25);
}

.rent-cron-switch-spinner {
    width: 15px;
    height: 15px;
    border: 2px solid #e2e8f0;
    border-top-color: #5b2e8e;
    border-radius: 50%;
    animation: rent-cron-spin 0.65s linear infinite;
}

@keyframes rent-cron-spin {
    to {
        transform: rotate(360deg);
    }
}

.rent-cron-switch--busy:disabled {
    opacity: 1;
    cursor: wait;
}

.rent-cron-label {
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
    min-width: 28px;
    text-align: right;
}

.rent-cron-label--on {
    color: #059669;
}

.rent-cron-switch {
    position: relative;
    width: 48px;
    height: 28px;
    border-radius: 999px;
    border: none;
    background: #cbd5e1;
    cursor: pointer;
    transition: background 0.2s ease;
    padding: 0;
    flex-shrink: 0;
}

.rent-cron-switch:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.rent-cron-switch--on {
    background: linear-gradient(135deg, #10b981, #059669);
}

.rent-cron-switch-knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;
}

.rent-cron-switch--on .rent-cron-switch-knob {
    transform: translateX(20px);
}

.rent-cron-error {
    margin: 10px 0 0 0;
    font-size: 13px;
    color: #b91c1c;
    text-align: left;
}

.rent-cron-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 24px;
}

.rent-cron-modal {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    max-width: 420px;
    width: 100%;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.rent-cron-modal-title {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    text-align: left;
}

.rent-cron-modal-body {
    margin: 0 0 20px 0;
    font-size: 14px;
    color: #475569;
    line-height: 1.5;
    text-align: left;
}

.rent-cron-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.rent-cron-btn-secondary {
    padding: 10px 18px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.rent-cron-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.rent-cron-btn-primary {
    padding: 10px 18px;
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.rent-cron-btn-primary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.rent-verify-overlay {
    z-index: 2100;
}

/* Payment proof modal — compact professional layout */
.proof-modal {
    width: 100%;
    max-width: min(92vw, 640px);
    max-height: min(92vh, 680px);
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
}

.proof-modal__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px 12px;
    border-bottom: 1px solid #f1f5f9;
    background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
    flex-shrink: 0;
}

.proof-modal__header-text {
    text-align: left;
    min-width: 0;
}

.proof-modal__title {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
}

.proof-modal__subtitle {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    line-height: 1.35;
}

.proof-modal__close {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, color 0.15s ease;
}

.proof-modal__close:hover:not(:disabled) {
    background: #e2e8f0;
    color: #0f172a;
}

.proof-modal__close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.proof-modal__body {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 0;
    flex: 1;
    overflow: hidden;
    text-align: left;
}

.proof-modal__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 18px 0;
    flex-shrink: 0;
    justify-content: flex-start;
}

.proof-modal__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.proof-modal__chip .status-dot {
    width: 5px;
    height: 5px;
}

.proof-modal__guest {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 18px 14px;
    flex-shrink: 0;
    text-align: left;
}

.proof-modal__avatar {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.proof-modal__guest-info {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
    text-align: left;
}

.proof-modal__guest-name {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
}

.proof-modal__guest-line {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    line-height: 1.45;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    text-align: left;
}

.proof-modal__guest-phone {
    font-variant-numeric: tabular-nums;
}

.proof-modal__guest-dot {
    color: #cbd5e1;
    user-select: none;
}

.proof-modal__guest-amount {
    color: #059669;
    font-weight: 700;
}

.proof-modal__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 0 18px;
    min-height: 0;
    flex: 1;
    overflow: auto;
}

@media (min-width: 560px) {
    .proof-modal__grid {
        grid-template-columns: 1fr minmax(204px, 236px);
        align-items: start;
    }
}

.proof-modal__viewer {
    min-width: 0;
}

.proof-modal__viewer-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    text-align: left;
    align-items: stretch;
}

.proof-modal__section-head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4px;
    padding: 0 2px;
    width: 100%;
    text-align: left;
}

.proof-modal__section-head--aside {
    margin-bottom: 8px;
}

.proof-modal__section-title {
    font-size: 11px;
    font-weight: 700;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    text-align: left;
}

.proof-modal__section-hint {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
    text-align: left;
    line-height: 1.35;
    max-width: 100%;
}

.proof-modal__viewer-shell {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    background: #0f172a;
}

.proof-modal__viewer-canvas {
    min-height: 168px;
    max-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: radial-gradient(ellipse at center, #1e293b 0%, #0f172a 100%);
}

.proof-modal__img-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: zoom-in;
    border-radius: 8px;
    line-height: 0;
    transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.proof-modal__img-trigger:hover {
    box-shadow: 0 0 0 2px rgba(167, 139, 250, 0.65);
}

.proof-modal__img-trigger:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.55);
}

.proof-modal__img {
    max-width: 100%;
    max-height: 256px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    display: block;
    pointer-events: none;
}

.proof-modal__file-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    padding: 20px 16px;
    color: #94a3b8;
}

.proof-modal__file-icon {
    margin: 0 0 10px 0;
    display: block;
    opacity: 0.85;
}

.proof-modal__file-text {
    margin: 0 0 12px 0;
    font-size: 12px;
    line-height: 1.45;
}

.proof-modal__file-cta {
    display: inline-flex;
    font-size: 12px;
    font-weight: 600;
    color: #c4b5fd;
    text-decoration: none;
    padding: 8px 14px;
    border-radius: 8px;
    background: rgba(124, 58, 237, 0.35);
    border: 1px solid rgba(167, 139, 250, 0.35);
    align-self: flex-start;
}

.proof-modal__file-cta:hover {
    background: rgba(124, 58, 237, 0.55);
    color: #fff;
}

.proof-modal__viewer-empty {
    border-radius: 12px;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    padding: 24px 16px;
    text-align: left;
    color: #64748b;
}

.proof-modal__viewer-empty svg {
    display: block;
    margin-bottom: 8px;
}

.proof-modal__viewer-empty p {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
}

.proof-modal__aside {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #f8fafc;
    padding: 14px 14px 12px;
    align-self: stretch;
    text-align: left;
}

.proof-modal__kv {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: left;
}

.proof-modal__kv li {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #e2e8f0;
    line-height: 1.4;
    text-align: left;
}

.proof-modal__kv li:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.proof-modal__kv li span:first-child {
    color: #64748b;
    font-weight: 700;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.proof-modal__kv li span:last-child {
    color: #0f172a;
    font-weight: 500;
    font-size: 13px;
    text-align: left;
    word-break: break-word;
    width: 100%;
    line-height: 1.45;
}

.proof-modal__note {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #e2e8f0;
    text-align: left;
}

.proof-modal__note--warn {
    background: #fef2f2;
    border-color: #fecaca;
}

.proof-modal__note-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
    text-align: left;
}

.proof-modal__note--warn .proof-modal__note-label {
    color: #b91c1c;
}

.proof-modal__note-body {
    margin: 0;
    font-size: 12px;
    color: #334155;
    line-height: 1.45;
    white-space: pre-wrap;
    text-align: left;
}

.proof-modal__note--warn .proof-modal__note-body {
    color: #991b1b;
}

.proof-modal__error {
    margin: 0 18px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
}

.proof-modal__reject {
    padding: 10px 18px 0;
    flex-shrink: 0;
    text-align: left;
}

.proof-modal__reject-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
}

.proof-modal__textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    min-height: 64px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.proof-modal__textarea:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}

.proof-modal__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 18px 16px;
    margin-top: auto;
    border-top: 1px solid #f1f5f9;
    background: #fafbfc;
    flex-shrink: 0;
    text-align: left;
}

.proof-modal__footer-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
    margin-left: auto;
}

.proof-modal__btn {
    padding: 9px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s ease, transform 0.1s ease;
}

.proof-modal__btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.proof-modal__btn--ghost {
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.proof-modal__btn--ghost:hover:not(:disabled) {
    background: #f8fafc;
    color: #0f172a;
}

.proof-modal__btn--primary {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: #fff;
    box-shadow: 0 2px 8px rgba(91, 46, 142, 0.25);
}

.proof-modal__btn--primary:hover:not(:disabled) {
    filter: brightness(1.05);
}

.proof-modal__btn--danger {
    background: #dc2626;
    color: #fff;
}

.proof-modal__btn--danger:hover:not(:disabled) {
    background: #b91c1c;
}

/* Full-screen proof preview (click thumbnail in modal) */
.proof-lightbox {
    position: fixed;
    inset: 0;
    z-index: 2200;
    background: rgba(2, 6, 23, 0.94);
    backdrop-filter: blur(6px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 56px 20px 48px;
    animation: proof-lightbox-in 0.2s ease;
}

@keyframes proof-lightbox-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.proof-lightbox__close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, color 0.15s ease;
}

.proof-lightbox__close:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
}

.proof-lightbox__frame {
    flex: 1;
    min-height: 0;
    width: 100%;
    max-width: min(96vw, 1280px);
    display: flex;
    align-items: center;
    justify-content: center;
}

.proof-lightbox__img {
    max-width: 100%;
    max-height: min(85vh, 900px);
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
}

.proof-lightbox__hint {
    position: absolute;
    bottom: 14px;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
    text-align: center;
    max-width: 90vw;
}

/* Filters Section */
.filters-section {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    flex-wrap: wrap;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.search-container {
    flex: 1;
    min-width: 250px;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    z-index: 1;
    width: 18px;
    height: 18px;
}

.search-input {
    width: 100%;
    padding: 10px 14px 10px 40px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background: #f8fafc;
    transition: all 0.2s ease;
    outline: none;
}

.search-input:focus {
    border-color: #5b2e8e;
    background: white;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.filter-controls {
    display: flex;
    gap: 10px;
}

.filter-select {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    font-size: 14px;
    font-weight: 500;
    color: #1e293b;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    min-width: 130px;
}

.filter-select:focus {
    border-color: #5b2e8e;
    background: white;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

/* Stats Section */
.stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 0;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.stat-card {
    background: white;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.stat-icon svg {
    width: 20px;
    height: 20px;
}

.stat-icon.paid {
    background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.due {
    background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-icon.pending-proof {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.stat-icon.total {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
}

.stat-content {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.stat-content h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 2px 0;
    line-height: 1.2;
    text-align: left;
}

.stat-content p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
    text-align: left;
}

/* Table Section */
.table-section {
    background: white;
    border-radius: 0;
    box-shadow: none;
    border: none;
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.table-header {
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.table-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.export-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.export-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.export-btn svg {
    width: 16px;
    height: 16px;
}

.modern-table-container {
    overflow-x: auto;
    overflow-y: auto;
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
    -webkit-overflow-scrolling: touch;
}

.modern-table-container.table-loading {
    opacity: 0.7;
}

/* Desktop: Allow table to scroll if needed but maximize space */
@media (min-width: 769px) {
    .modern-table-container {
        overflow-y: auto;
        max-height: none;
    }
    
    .table-section {
        overflow: hidden;
        flex: 1;
        min-height: 0;
    }
}

/* Table Loading Overlay */
.table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    border-radius: 8px;
}

.table-spinner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.spinner-circle-small {
    width: 20px;
    height: 20px;
    border: 2px solid #e2e8f0;
    border-top: 2px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text-small {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    margin: 0;
}

.table-wrapper {
    min-width: 800px;
    width: 100%;
}

.modern-table {
    width: 100%;
    border-collapse: collapse;
}

.modern-table th {
    background: #f8fafc;
    padding: 12px 16px;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
    white-space: nowrap;
    vertical-align: middle;
}

.modern-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    position: relative;
    padding-right: 32px;
}

.modern-table th.sortable:hover {
    background: #f1f5f9;
    color: #5b2e8e;
}

.sort-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;
    width: 16px;
    height: 16px;
    display: inline-block;
}

.modern-table th.sortable:hover .sort-icon {
    opacity: 1;
}

.modern-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    color: #1e293b;
    white-space: nowrap;
    vertical-align: middle;
    text-align: left;
}

.table-row {
    transition: background-color 0.2s ease;
}

.table-row:hover {
    background: #f8fafc;
}

/* Table Cell Styles */
.payment-id {
    text-align: left;
    vertical-align: middle;
}

.payment-id .id-badge {
    background: #f3f4f6;
    color: #6b7280;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Monaco', monospace;
    display: inline-block;
}

.guest-info {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: normal;
    text-align: left;
    vertical-align: middle;
}

.guest-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.guest-details {
    display: flex;
    flex-direction: column;
}

.guest-name {
    font-weight: 600;
    color: #1f2937;
}

.guest-mobile {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    margin-top: 2px;
}

.guest-id {
    font-size: 12px;
    color: #6b7280;
}

.mobile-cell {
    font-weight: 500;
    text-align: left;
    vertical-align: middle;
}

.mobile-number {
    color: #374151;
    font-family: 'Monaco', monospace;
    font-size: 14px;
    background: #f3f4f6;
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 600;
    display: inline-block;
}

.object-tag {
    background: #ede9fe;
    color: #7c3aed;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
}

.amount-cell {
    text-align: left;
    vertical-align: middle;
}

.amount {
    font-weight: 700;
    color: #059669;
    font-size: 16px;
    display: inline-block;
}

.period-cell {
    text-align: left;
    vertical-align: middle;
}

.period {
    background: #fef3c7;
    color: #d97706;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
}

.payment-mode {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    display: inline-block;
}

.date-cell {
    text-align: left;
    vertical-align: middle;
}

.actions-cell {
    text-align: left;
    vertical-align: middle;
}

/* Status Badges */
.modern-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    vertical-align: middle;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}

.status-paid {
    background: #d1fae5;
    color: #065f46;
}

.status-paid .status-dot {
    background: #10b981;
}

.status-due {
    background: #fef3c7;
    color: #92400e;
}

.status-due .status-dot {
    background: #f59e0b;
}

.status-failed {
    background: #fee2e2;
    color: #991b1b;
}

.status-failed .status-dot {
    background: #ef4444;
}

.status-rent-review {
    background: #e0f2fe;
    color: #0369a1;
}

.status-rent-review .status-dot {
    background: #0ea5e9;
}

.status-unknown {
    background: #f3f4f6;
    color: #6b7280;
}

.status-unknown .status-dot {
    background: #9ca3af;
}

.proof-badge {
    text-transform: none;
    font-size: 11px;
}

.status-proof-verified {
    background: #d1fae5;
    color: #065f46;
}

.status-proof-verified .status-dot {
    background: #10b981;
}

.status-proof-rejected {
    background: #fee2e2;
    color: #991b1b;
}

.status-proof-rejected .status-dot {
    background: #ef4444;
}

.status-proof-pending {
    background: #e0e7ff;
    color: #3730a3;
}

.status-proof-pending .status-dot {
    background: #6366f1;
}

.status-proof-review {
    background: #cffafe;
    color: #0e7490;
}

.status-proof-review .status-dot {
    background: #06b6d4;
}

.status-proof-none {
    background: #f3f4f6;
    color: #6b7280;
}

.status-proof-none .status-dot {
    background: #9ca3af;
}

.review-proof-btn,
.view-proof-btn {
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #5b2e8e;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.review-proof-btn:hover,
.view-proof-btn:hover {
    background: #5b2e8e;
    color: #fff;
    border-color: #5b2e8e;
}

.actions-placeholder {
    color: #94a3b8;
    font-size: 14px;
}

/* Action Buttons */
.action-dropdown-container {
    position: relative;
    pointer-events: auto;
}

.modern-action-btn {
    width: 36px;
    height: 36px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.modern-action-btn:hover {
    background: #5b2e8e;
    color: white;
    border-color: #5b2e8e;
}

.modern-action-dropdown {
    position: fixed;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    min-width: 160px;
    overflow: hidden;
    pointer-events: auto;
}

.action-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.action-item:hover {
    background: #f8fafc;
}

.action-item.paid:hover {
    background: #ecfdf5;
    color: #065f46;
}

.action-item.due:hover {
    background: #fffbeb;
    color: #92400e;
}

/* Empty State */
.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #6b7280;
}

.empty-icon {
    margin-bottom: 16px;
    opacity: 0.5;
    display: flex;
    justify-content: center;
    align-items: center;
}

.empty-illustration-img {
    width: 200px;
    height: 200px;
    max-width: 100%;
    object-fit: contain;
}

.empty-state h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #374151;
}

.empty-state p {
    font-size: 14px;
    margin: 0;
}

/* Modern Pagination */
.modern-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: white;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.pagination-info {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pagination-arrow {
    width: 40px;
    height: 40px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.pagination-arrow:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    gap: 4px;
    margin: 0 8px;
}

.page-btn {
    width: 40px;
    height: 40px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #374151;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled):not(.active):not(.ellipsis) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.page-btn.active {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-color: #5b2e8e;
    color: white;
}

.page-btn.ellipsis {
    cursor: default;
    border: none;
    background: none;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .filters-section,
    .stats-section {
        padding: 12px 16px;
    }
    
    .stats-section {
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 10px;
    }
    
    .stat-card {
        padding: 12px 14px;
    }
}

@media (max-width: 768px) {
    .filters-section,
    .stats-section {
        padding: 12px;
    }
    
    .header-section {
        padding: 16px;
    }
    
    .header-content {
        flex-direction: row;
        gap: 12px;
        align-items: center;
        justify-content: space-between;
    }
    
    .title-section {
        flex: 1;
        min-width: 0;
        gap: 12px;
    }
    
    .title-icon {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
    }
    
    .title-text {
        min-width: 0;
        flex: 1;
    }
    
    .title-text h1 {
        font-size: 20px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .title-text p {
        display: none;
    }
    
    .back-btn {
        padding: 10px 16px;
        font-size: 14px;
        flex-shrink: 0;
    }
    
    .back-btn svg {
        width: 18px;
        height: 18px;
    }
    
    .filters-section {
        flex-direction: column;
    }
    
    .search-container {
        min-width: auto;
    }
    
    .filter-controls {
        flex-wrap: wrap;
    }
    
    .stats-section {
        grid-template-columns: 1fr;
    }
    
    .table-header {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
        padding: 16px;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
    }
    
    .table-wrapper {
        min-width: 800px;
        display: table;
    }
    
    .modern-table {
        display: table;
        width: 100%;
    }
    
    .modern-pagination {
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }
    
    .pagination-info {
        text-align: center;
    }
}

@media (max-width: 640px) {
    .header-section {
        padding: 12px;
        margin-top: 10px;
    }
    
    .header-content {
        gap: 8px;
    }
    
    .title-icon {
        width: 36px;
        height: 36px;
    }
    
    .logo-image {
        width: 20px;
        height: 20px;
    }
    
    .title-text h1 {
        font-size: 18px;
    }
    
    .back-btn {
        padding: 8px 12px;
        font-size: 12px;
        gap: 6px;
    }
    
    .back-btn svg {
        width: 16px;
        height: 16px;
    }
    
    .modern-table th,
    .modern-table td {
        padding: 10px 6px;
        font-size: 12px;
        vertical-align: middle;
        text-align: left;
    }
    
    .modern-table th {
        font-size: 12px;
        padding: 12px 6px;
    }
    
    .modern-table th.sortable {
        padding-right: 24px;
    }
    
    .sort-icon {
        right: 6px;
        width: 12px;
        height: 12px;
    }
    
    .guest-info {
        flex-direction: row;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
    }
    
    .guest-details {
        min-width: 0;
        overflow: hidden;
    }
    
    .guest-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 120px;
        display: inline-block;
    }
    
    .guest-avatar {
        width: 28px;
        height: 28px;
        font-size: 12px;
    }
    
    .table-section {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
        flex: 1;
        min-height: 0;
    }
    
    .table-wrapper {
        min-width: 900px;
        display: table;
        width: 100%;
    }
    
    .modern-table {
        display: table;
        width: 100%;
        table-layout: auto;
    }
    
    .modern-table th,
    .modern-table td {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .modern-table th {
        white-space: nowrap;
    }
    
    .payment-id,
    .mobile-cell,
    .amount-cell,
    .period-cell,
    .date-cell,
    .actions-cell {
        white-space: nowrap;
    }
}

@media (max-width: 480px) {
    .header-section {
        padding: 10px;
        margin-top: 8px;
    }
    
    .header-content {
        gap: 6px;
    }
    
    .title-icon {
        width: 32px;
        height: 32px;
    }
    
    .logo-image {
        width: 18px;
        height: 18px;
    }
    
    .title-text h1 {
        font-size: 16px;
    }
    
    .back-btn {
        padding: 6px 10px;
        font-size: 12px;
        gap: 4px;
    }
    
    .back-btn svg {
        width: 14px;
        height: 14px;
    }
    
    .filters-section {
        padding: 10px;
        gap: 10px;
    }
    
    .search-input {
        padding: 10px 12px 10px 40px;
        font-size: 14px;
    }
    
    .filter-select {
        padding: 10px 12px;
        font-size: 12px;
        min-width: 120px;
    }
    
    /* Stats Section - Proper Alignment */
    .stats-section {
        padding: 12px;
        gap: 10px;
        grid-template-columns: 1fr;
        justify-items: stretch;
    }
    
    .stat-card {
        padding: 16px;
        gap: 12px;
        align-items: center;
        justify-content: flex-start;
        text-align: left;
    }
    
    .stat-icon {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
    }
    
    .stat-icon svg {
        width: 20px;
        height: 20px;
    }
    
    .stat-icon .logo-image {
        width: 20px;
        height: 20px;
    }
    
    .stat-content {
        flex: 1;
        min-width: 0;
        text-align: left;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
    
    .stat-content h3 {
        font-size: 20px;
        margin: 0 0 2px 0;
        word-break: break-word;
        text-align: left;
        width: 100%;
    }
    
    .stat-content p {
        font-size: 12px;
        margin: 0;
        text-align: left;
        width: 100%;
    }
    
    .table-header {
        padding: 12px;
    }
    
    .table-header h2 {
        font-size: 16px;
    }
    
    .export-btn {
        padding: 8px 12px;
        font-size: 12px;
    }
    
    .export-btn svg {
        width: 16px;
        height: 16px;
    }
    
    /* Table - Show all rows without vertical scroll */
    .table-section {
        flex: 0 1 auto;
        min-height: auto;
        display: flex;
        flex-direction: column;
        overflow: visible;
        background: white;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        flex: 0 1 auto;
        min-height: auto;
        max-height: none;
        height: auto;
        position: relative;
    }
    
    .table-wrapper {
        display: table;
    }
    
    .modern-table tbody {
        display: table-row-group;
    }
    
    .modern-table tr {
        display: table-row;
    }
    
    .table-wrapper {
        min-width: 850px;
        display: table;
        width: 100%;
    }
    
    .modern-table {
        display: table;
        width: 100%;
        table-layout: auto;
    }
    
    .modern-table th {
        padding: 10px 8px;
        font-size: 12px;
        white-space: nowrap;
        vertical-align: middle;
        text-align: left;
    }
    
    .modern-table th.sortable {
        padding-right: 24px;
    }
    
    .modern-table td {
        padding: 10px 8px;
        font-size: 12px;
        white-space: nowrap;
        vertical-align: middle;
        text-align: left;
    }
    
    .sort-icon {
        width: 12px;
        height: 12px;
        right: 8px;
    }
    
    .guest-avatar {
        width: 24px;
        height: 24px;
        font-size: 12px;
    }
    
    .guest-info {
        gap: 6px;
    }
    
    .guest-name {
        max-width: 100px;
        font-size: 12px;
    }
    
    .id-badge {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .mobile-number {
        font-size: 12px;
        padding: 3px 6px;
    }
    
    .object-tag {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .amount {
        font-size: 12px;
    }
    
    .period {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .payment-mode {
        font-size: 12px;
    }
    
    .modern-status-badge {
        padding: 4px 8px;
        font-size: 12px;
    }
    
    .status-dot {
        width: 4px;
        height: 4px;
    }
    
    .modern-action-btn {
        width: 28px;
        height: 28px;
    }
    
    .modern-action-btn svg {
        width: 16px;
        height: 16px;
    }
    
    .modern-pagination {
        padding: 12px;
        gap: 12px;
    }
    
    .pagination-info {
        font-size: 12px;
    }
    
    .pagination-arrow {
        width: 32px;
        height: 32px;
    }
    
    .pagination-arrow svg {
        width: 16px;
        height: 16px;
    }
    
    .page-btn {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }
    
    .empty-illustration-img {
        width: 150px;
        height: 150px;
    }
}
</style>